class PdpGallery extends HTMLElement {
  constructor() {
    super();
    // Elements
    this.thumbnails = this.querySelectorAll(".fa-pdp-specs__thumbnail");
    this.rawDatas = this.querySelectorAll(".media-data");
    this.parsedData_array = this.parseData();

    this.activeThumbnail = 0;
    this.activeMedia = 0;
    this.allMedia = this.parsedData_array
    .filter(entry => entry.media && Array.isArray(entry.media))
    .flatMap(entry => entry.media);
    this.mediaList = this.allMedia;
    this.media = this.mediaList[this.activeMedia];

    // DOM Elements
    this.navigation = this.querySelector(".fa-pdp-specs__content-navigation");
    this.tabContainer = this.querySelector(".fa-pdp-specs__dropdown");
    this.tabHeader = this.tabContainer?.querySelector("summary span");
    this.tabContents = this.tabContainer?.querySelector(".details__content-inner");

    this.textOverlay = this.querySelector(".fa-pdp-specs__text-overlay");
    this.textOverlayContent = this.querySelector(".fa-pdp-specs__text-overlay-content");
    this.progressBar = this.querySelector(".fa-pdp-specs__progress-bar");
    this.mediaContainer = this.querySelector(".fa-pdp-specs__media");
    this.model = this.mediaContainer?.querySelector("model-viewer");
    this.interactButton = this.querySelector(".fa-pdp-specs__interact");
    this.svgContainer = this.querySelector(".fa-pdp-specs__svg-container");
    this.section = this.closest(".shopify-section");
    this.tableButton = this.section?.querySelector(".pdp-specs__table-button span");
    this.tableContainer = this.section?.querySelector(".pdp-specs__table-container");

    // Misc
    this.srcset = [300, 400, 500, 600, 700, 800, 900, 1000, 1100, 1200, 1400];
    this.promptShown = false;
    this.startX = 0;
    this.direction = 0;
    this.scrubSensitivity = 0.01;
  }

  connectedCallback() {
    // console.log("INIT PDP gallery", this.parsedData_array);
    this.updateMainContent();
    this.initTabs();
    this.initListeners();
  }

  initListeners() {
    if (this.model) {
      this.model.addEventListener("load", () => {
        this.createHotspot(this.media);
      });
    }

    // Tabs restored
    this.initTabListener();

    if (this.interactButton) {
      this.interactButton.addEventListener("click", this.startInteraction.bind(this));
    }

    if (this.tableButton && this.tableContainer) {
      this.tableButton.addEventListener("click", () => {
        const collapsed = this.tableContainer.dataset.state === "collapse";
        this.tableContainer.dataset.state = collapsed ? "expand" : "collapse";
        this.tableButton.textContent = collapsed ? "Hide Specs" : "View All Specs";
      });
    }
  }

  startInteraction() {
    if (!this.model || !this.interactButton) return;

    if (this.interactButton.dataset.action === "stop-interaction") {
      this.model.removeAttribute("interaction-prompt-threshold");
      this.model.setAttribute("interaction-prompt", "none");
      this.model.classList.remove("start-interaction");
      this.interactButton.dataset.action = "start-interaction";
      this.interactButton.querySelector("span").innerHTML = "Interact";
      this.interactButton.setAttribute("aria-label", "Start interacting with the model");
      this.svgContainer.innerHTML = `
        <svg width="25" height="26" viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation">
          <path d="M12.6787 0.783203C13.8235 0.783315 14.8725 1.48971 15.7432 2.66797C16.6035 3.83229 17.3021 5.47355 17.7637 7.41016C19.7638 7.86157 21.463 8.56447 22.6699 9.43555C23.8903 10.3164 24.625 11.3845 24.625 12.5518C24.6248 13.7034 23.9098 14.758 22.7188 15.6318C21.5417 16.4953 19.8831 17.1938 17.9277 17.6523C17.5022 19.7907 16.7979 21.6193 15.9053 22.9209C15.0035 24.2358 13.8952 25.0331 12.6787 25.0332Z" fill="#121212" stroke="#121212" stroke-width="0.25"/>
        </svg>
      `;
    } else {
      this.model.setAttribute("interaction-prompt-threshold", 1000);
      this.model.setAttribute("interaction-prompt", "auto");
      this.model.classList.add("start-interaction");
      this.interactButton.dataset.action = "stop-interaction";
      this.interactButton.setAttribute("aria-label", "Stop interacting with the model");
      this.interactButton.querySelector("span").innerHTML = "Exit";
      this.svgContainer.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation">
          <path d="M1.4 14L0 12.6L5.6 7L0 1.4L1.4 0L7 5.6L12.6 0L14 1.4L8.4 7L14 12.6L12.6 14L7 8.4L1.4 14Z" fill="#121212"/>
        </svg>
      `;
    }
  }

  parseData() {
    let data_array = [];
    this.rawDatas.forEach(rawData => {
      if (rawData.textContent.trim() !== "") {
        try {
          data_array.push(JSON.parse(rawData.textContent));
        } catch (e) {
          console.warn("Failed to parse media-data JSON:", e);
        }
      }
    });
    return data_array;
  }

  initTabs() {
    for (let i = 0; i < this.mediaList.length; i++) {
      let stepTitle = `Step ${i + 1}`;
      let e_class = '';
      if (i == 0) {
        e_class = 'active';
      }
      if (this.mediaList[i].title !== undefined) stepTitle = this.mediaList[i].title;
      this.tabContents.innerHTML += this.createTab(stepTitle, i, e_class);
    }
  }

  createTab(title, index, e_class) {
    let mediaSrc = this.mediaList[index].media.split('?')[0].split('/');
    mediaSrc = mediaSrc[mediaSrc.length - 1].replace('.glb','.jpg').replace('_.jpg', '.jpg');
    // https://freakathlete.co/cdn/shop/files/preview_images/HALF_BENCH.jpg?v=1761084896&width=1024

    return `<button data-index="${index}" class="v-stack gap-2 ${e_class}">
      <div class="image-container relative w-full " id="image--" style="padding-top: 100.0%;">
        <img src="//freakathlete.co/cdn/shop/files/preview_images/${mediaSrc}?v=1761084894&amp;width=300" width="100" height="100" loading="lazy" fetch="low" sizes="75px" role="presentation">
      </div>
      <span class="relative">${title}</span>
    </button>`;
  }

  initTabListener() {
    if (!this.tabContents) return;
    const buttons = this.tabContents.querySelectorAll("button");
    buttons.forEach(button => {
      button.addEventListener("click", () => {
        const index = parseInt(button.dataset.index);
        if (!isNaN(index)) {
          this.activeMedia = index;
          this.media = this.mediaList[this.activeMedia];
          this.updateMainContent();

          buttons.forEach(b => b.classList.remove("active"));
          button.classList.add("active");

          if (this.tabContainer) this.tabContainer.removeAttribute("open");
          if (this.tabHeader) this.tabHeader.textContent = button.textContent;
        }
      });
    });
  }

  getSrc() {
    if (!this.media) return;
    let src = this.media.media;
    // if (window.innerWidth < 1000 && this.media.mediaMobile != '') {
    //   src = this.media.mediaMobile;
    // }
    this.source = src.startsWith("//") ? "https:" + src : src;
  }

  updateModel() {
    if (!this.model) return;
    this.model.src = this.source;
    this.model.style.display = "";
    // this.model.play();
  }

  updateMainContent() {
    if (!this.media) return;
    this.getSrc();
    this.updateModel();
    this.createHotspot();
    this.updateTextOverlay();
  }

  createHotspot() {
    if (!this.model) return;
    this.model.innerHTML = "";
    for (let i = 1; i <= 5; i++) {
      let label_h = `hotspot_label_${i}`;
      let position_h = `hotspot_position_${i}`;
      let normal_h = `hotspot_normal_${i}`;
      let modal_h = `hotspot_modal_${i}`;

      if (this.media[label_h]) {
        let measurements = this.media[label_h].split("||");
        this.model.innerHTML += `
          <div slot="hotspot-${i}" data-position="${this.media[position_h]}" data-normal="${this.media[normal_h]}" class="hotspot" role="button" aria-controls="${this.media[modal_h]}" aria-expanded="false" aria-haspopup="dialog" tabIndex="0">
            <span class="metric hotspot-label" data-unit-value="metric">${measurements[1] || ""}</span>
            <span class="imperial hotspot-label" data-unit-value="imperial">${measurements[0] || ""}</span>
          </div>`;
      }
    }
  }

  updateTextOverlay() {
    if (!this.textOverlayContent) return;
    this.textOverlayContent.innerHTML = "";
    if (!this.media) return;

    let textClass = this.media.tab ? "text-center" : "";
    if (this.media.title && !this.media.tab) {
      this.textOverlayContent.innerHTML += `<p class="h4">${this.media.title}</p>`;
    }
    if (this.media.description) {
      this.textOverlayContent.innerHTML += `<p class="text-xs ${textClass}">${this.media.description}</p>`;
    }
  }
}

customElements.define("pdp-gallery", PdpGallery);
